import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Search, X, Package } from "lucide-react";
import Config from "../../_config/config";
import ProductCmsService from "../../_services/ProductCmsService";

interface Product {
  id: number;
  name: string;
  partnerName?: string | null;
  category?: string | null;
  imageUrl?: string | null;
  productCode?: string | null;
}

interface Props {
  onPick: (product: Product, url: string) => void;
  triggerLabel?: string;
  variant?: "default" | "compact";
}

interface Anchor {
  top: number;
  left: number;
  width: number;
}

const PANEL_WIDTH = 380;
const PANEL_MAX_HEIGHT = 380;

/**
 * Inline searchable product picker. Renders the panel into a body-portal with
 * fixed positioning anchored to the trigger button so it escapes any
 * ancestor's overflow:hidden / scroll clipping (modal body, section card, etc).
 */
const ProductPicker: React.FC<Props> = ({ onPick, triggerLabel = "Pick from products", variant = "default" }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [anchor, setAnchor] = useState<Anchor | null>(null);

  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 200);
    return () => clearTimeout(t);
  }, [search]);

  // Fetch products when panel opens or search changes
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    ProductCmsService.listProducts(debounced || undefined)
      .then((res: any) => {
        if (cancelled) return;
        setProducts((res?.data?.data ?? []) as Product[]);
      })
      .catch((e: any) => {
        if (cancelled) return;
        setErr(e?.response?.data?.error?.message || e?.message || "Failed to load products");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [debounced, open]);

  // Position the panel under the trigger; recompute on scroll/resize
  const updateAnchor = () => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (!r) return;
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;

    // Prefer below-and-left-aligned; flip up if not enough room below
    const wantedTop = r.bottom + 6;
    const wantedLeft = Math.min(r.left, viewportW - PANEL_WIDTH - 12);
    const spaceBelow = viewportH - r.bottom;
    const top = spaceBelow < PANEL_MAX_HEIGHT + 20 && r.top > PANEL_MAX_HEIGHT
      ? r.top - PANEL_MAX_HEIGHT - 6
      : wantedTop;

    setAnchor({
      top,
      left: Math.max(12, wantedLeft),
      width: PANEL_WIDTH,
    });
  };

  useLayoutEffect(() => {
    if (!open) return;
    updateAnchor();
    const handler = () => updateAnchor();
    window.addEventListener("scroll", handler, true); // capture: catches scroll on ancestors
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("scroll", handler, true);
      window.removeEventListener("resize", handler);
    };
  }, [open]);

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const onMouse = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        panelRef.current?.contains(t) ||
        triggerRef.current?.contains(t)
      ) {
        return;
      }
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onMouse);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onMouse);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Always derive the canonical public ilearn URL — NOT Config.ilearnUrl —
  // because:
  //   1. The CTA URL ends up on a public verification page opened by anonymous
  //      LinkedIn / employer click-throughs. Config.ilearnUrl honours the dev
  //      override (REACT_APP_ILEARN_URL=http://localhost:3001), which would
  //      leak a localhost URL into the signed credential.
  //   2. Production ilearn lives at {tenant}.ilearn.edusquares.com — the bare
  //      {tenant}.edusquares.com is the legacy Java tuningfork app.
  // Tenants on a custom domain can edit the URL inline after picking.
  const productUrl = (p: Product) =>
    `https://${Config.site}.ilearn.edusquares.com/ProductDetails/${p.id}`;

  const handlePick = (p: Product) => {
    onPick(p, productUrl(p));
    setOpen(false);
    setSearch("");
  };

  return (
    <div className={`bm-product-picker ${variant === "compact" ? "is-compact" : ""}`}>
      <button
        type="button"
        ref={triggerRef}
        className="bm-product-picker-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <Package size={variant === "compact" ? 12 : 14} />
        {triggerLabel}
      </button>

      {open && anchor && createPortal(
        <div
          ref={panelRef}
          className="bm-product-picker-panel"
          style={{
            position: "fixed",
            top: anchor.top,
            left: anchor.left,
            width: anchor.width,
            maxHeight: PANEL_MAX_HEIGHT,
          }}
          role="dialog"
        >
          <div className="bm-product-picker-search">
            <Search size={14} className="bm-product-picker-search-icon" />
            <input
              type="text"
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search products by name or code…"
            />
            {search && (
              <button
                type="button"
                className="bm-product-picker-clear"
                onClick={() => setSearch("")}
                aria-label="Clear search"
              >
                <X size={12} />
              </button>
            )}
          </div>

          <div className="bm-product-picker-list">
            {loading ? (
              <div className="bm-product-picker-state">Loading…</div>
            ) : err ? (
              <div className="bm-product-picker-state error">{err}</div>
            ) : products.length === 0 ? (
              <div className="bm-product-picker-state">No products match your search.</div>
            ) : (
              products.slice(0, 50).map((p) => (
                <button
                  type="button"
                  key={p.id}
                  className="bm-product-picker-row"
                  onClick={() => handlePick(p)}
                  title={productUrl(p)}
                >
                  <div className="bm-product-picker-thumb">
                    {p.imageUrl ? (
                      <img src={p.imageUrl} alt="" />
                    ) : (
                      <Package size={18} />
                    )}
                  </div>
                  <div className="bm-product-picker-meta">
                    <div className="bm-product-picker-name">{p.name}</div>
                    <div className="bm-product-picker-sub">
                      {p.partnerName ? <span>{p.partnerName}</span> : null}
                      {p.partnerName && p.category ? <span> · </span> : null}
                      {p.category ? <span>{p.category}</span> : null}
                      {p.productCode ? (
                        <code style={{ marginLeft: 6 }}>{p.productCode}</code>
                      ) : null}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default ProductPicker;
